import trackSelectedHomeDeliveryAddress from '@helpers/analyticsHelpers/trackSelectedHomeDeliveryAddress';
import webToAppApi from '@api/web-to-app';
import logger from '@logger';
import useCheckCartConflict from '@hooks/useCheckCartConflict';
import useCustomer from '@hooks/useCustomer';
import checkoutHelper from '@helpers/checkoutHelper';
import {
  CartAddressType,
  deleteSlotInCart,
  setAddressOnCart,
  setDeliveryMode,
  setPostalCodeOnCart,
} from '@api/interfaces/cartApi';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';
import useAbortController from '@hooks/useAbortController';

export default function useFinishSubmission(theme: string, afterSubmit?: () => void) {
  const { customer } = useCustomer();
  const checkout = checkoutHelper();
  const { refreshCart } = useCart();
  const checkCartConflict = useCheckCartConflict();
  const { store, refreshStore } = useStore();
  const getSignal = useAbortController();

  const handleStoreSwitch = async (deliveryStoreId: string) => {
    const signal = getSignal();
    const { activateStore } = await import('@api/interfaces/storeApi');
    const response = await activateStore(deliveryStoreId, true, true, signal);

    await refreshStore(response.data);
    await deleteSlotInCart();
  };

  return async (
    deliveryStoreId: string,
    addressParams: AddressParamsType,
    prefetchedAddressData: CustomerCoordinatesType,
    callback: () => void
  ) => {
    try {
      await checkCartConflict({ storeId: deliveryStoreId });
      await setDeliveryMode('homeDelivery', '');

      if (store?.storeId !== deliveryStoreId || !store?.activelySelected) {
        await handleStoreSwitch(deliveryStoreId);
      }

      const setAddressParams: CartAddressType = {
        addressLine: addressParams.addressLine || '',
        addressLine2: addressParams.addressLine2 || customer?.defaultShippingAddress?.line2 || '',
        postalCode: addressParams.postalCode || '',
        locality: addressParams.locality || '',
        longitude: prefetchedAddressData?.longitude?.toString() || '',
        latitude: prefetchedAddressData?.latitude?.toString() || '',
        firstName:
          prefetchedAddressData?.firstName || customer?.defaultShippingAddress?.firstName || checkout?.firstName || '',
        lastName:
          prefetchedAddressData?.lastName || customer?.defaultShippingAddress?.lastName || checkout?.lastName || '',
        cellphone: customer?.defaultShippingAddress?.cellphone || checkout?.cellphone || '',
        email: customer?.email || checkout?.email || '',
        companyName:
          addressParams?.companyName ||
          customer?.companyAlternativeName ||
          customer?.defaultShippingAddress?.companyName ||
          undefined,
      };

      await setAddressOnCart(setAddressParams);
      if (addressParams.postalCode) {
        await setPostalCodeOnCart(addressParams.postalCode);
      }
      const cartData = await refreshCart();
      await refreshCart(cartData);

      trackSelectedHomeDeliveryAddress({ deliveryStoreId, theme });
      webToAppApi.eventDeliveryMethodChanged();
    } catch (err: any) {
      logger.error({ error: 'Could not finish home delivery submit', message: err.message });
      callback();
    } finally {
      if (callback) callback();
      if (afterSubmit) afterSubmit();
    }
  };
}
